import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, ArrowRight, Shield} from 'lucide-react'
import { lumi } from '@/lib/lumi'

export default function PreSelection() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    whatsapp: '',
    posicaoInvestimento: '',
    areasTravadas: '',
    padraoRepetitivo: '',
    tentativasResolucao: '',
    nivelComprometimento: '',
    disponibilidadeInvestir: '',
    umaCoisa: ''
  })
  const [showQuestion2, setShowQuestion2] = useState(false)
  const [showQuestion3, setShowQuestion3] = useState(false)
  const [showQuestion4, setShowQuestion4] = useState(false)
  const [showQuestion5, setShowQuestion5] = useState(false)


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
    
    // Redirecionar se escolher Opção C
    if (name === 'posicaoInvestimento' && value === 'Neste momento não tenho condições financeiras de investir.') {
      navigate('/nao-pronta')
    }
  }

  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault()
    if (step === 1) {
      // Validar dados pessoais e posição sobre investimento
      if (formData.nome && formData.email && formData.whatsapp && formData.posicaoInvestimento) {
        setStep(2)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Prevenir múltiplos envios
    if (isSubmitting) return
    
    setIsSubmitting(true)
    
    try {
      // Salvar no banco de dados
      const now = new Date().toISOString()
      await lumi.entities.preselection_submissions.create({
        nome: formData.nome,
        email: formData.email,
        whatsapp: formData.whatsapp,
        posicaoInvestimento: formData.posicaoInvestimento,
        areasTravadas: formData.areasTravadas,
        padraoRepetitivo: formData.padraoRepetitivo,
        tentativasResolucao: formData.tentativasResolucao,
        nivelComprometimento: formData.nivelComprometimento,
        disponibilidadeInvestir: formData.disponibilidadeInvestir,
        umaCoisa: formData.umaCoisa,
        motivacaoAgora: '',
        processosAnteriores: '',
        status: 'Pendente',
        createdAt: now,
        updatedAt: now
      })
      
      console.log('Form submitted successfully:', formData)
      
      // Redirecionar para página de confirmação
      navigate('/confirmacao')
    } catch (error) {
      console.error('Failed to submit form:', error)
      alert('Erro ao enviar formulário. Por favor, tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-deep via-violet-medium to-rose-deep/40">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-violet-dust/30">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button
            onClick={() => step === 1 ? navigate('/') : setStep(1)}
            className="flex items-center gap-2 text-violet-deep hover:text-violet-medium transition-colors font-sans text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center gap-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            step === 1 ? 'bg-white text-violet-deep shadow-lg' : 'bg-white/30 text-white'
          }`}>
            <span className="font-sans font-bold">1</span>
            <span className="font-sans text-sm font-medium hidden md:inline">Dados Pessoais</span>
          </div>
          <div className="w-12 h-0.5 bg-white/30"></div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            step === 2 ? 'bg-white text-violet-deep shadow-lg' : 'bg-white/30 text-white'
          }`}>
            <span className="font-sans font-bold">2</span>
            <span className="font-sans text-sm font-medium hidden md:inline">Perguntas</span>
          </div>
        </div>
      </div>

      {/* Form Container */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            {step === 1 ? 'Seus Dados' : 'Sobre Você'}
          </h1>
          <p className="font-sans text-violet-dust text-base md:text-lg">
            {step === 1 
              ? 'Se você está pronta para ir à raiz de uma vez, responda às perguntas abaixo com atenção e honestidade. Este processo foi criado para mulheres que já investiram em si mesmas e que estão prontas para uma transformação real e completa. Eu vou ler cada resposta com cuidado antes de entrar em contato.'
              : 'Responda com atenção e honestidade'}
          </p>
        </div>

        {/* ETAPA 1 - DADOS PESSOAIS */}
        {step === 1 && (
          <form onSubmit={handleNextStep} className="space-y-8">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-10 border border-violet-dust/30">
              <div className="space-y-5">
                <div>
                  <label className="block font-sans text-gray-700 font-medium mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    required
                    value={formData.nome}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label className="block font-sans text-gray-700 font-medium mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label className="block font-sans text-gray-700 font-medium mb-2">
                    WhatsApp (com DDD) *
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    required
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="flex items-start gap-2 bg-violet-dust/10 p-4 rounded-lg mt-6">
                  <Shield className="w-5 h-5 text-violet-deep flex-shrink-0 mt-0.5" />
                  <p className="font-sans text-sm text-gray-700">Seus dados estão seguros e não serão compartilhados com terceiros.</p>
                </div>
              </div>
            </div>

            {/* Pergunta sobre Investimento */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-10 border border-violet-dust/30">
              <label className="block font-sans text-gray-700 font-medium mb-4">
                Este é um processo individual de alta profundidade com 7 atendimentos. O investimento é compatível com processos terapêuticos premium. Como você está em relação a investir em si mesma nesse nível agora? *
              </label>
              <div className="space-y-3">
                {[
                  "Estou pronta para investir nesse nível se fizer sentido para mim.",
                  "Tenho interesse real mas preciso entender melhor antes de decidir.",
                  "Neste momento não tenho condições financeiras de investir."
                ].map((option) => (
                  <label key={option} className="flex items-center gap-3 p-3 rounded-lg hover:bg-violet-dust/10 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="posicaoInvestimento"
                      value={option}
                      required
                      onChange={handleChange}
                      className="w-4 h-4 text-violet-deep focus:ring-violet-medium"
                    />
                    <span className="font-sans text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <div className="text-center pt-4">
              <button
                type="submit"
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-rose-soft via-rose-deep to-pink-500 hover:from-pink-500 hover:via-rose-deep hover:to-rose-soft text-white font-sans font-bold text-lg px-12 py-5 rounded-full transition-all duration-300 shadow-[0_0_40px_rgba(236,72,153,0.8)] hover:shadow-[0_0_60px_rgba(236,72,153,1)] transform hover:scale-110 hover:-translate-y-2 animate-pulse"
              >
                Continuar para Perguntas
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </button>
            </div>
          </form>
        )}

        {/* ETAPA 2 - PERGUNTAS */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-10 border border-violet-dust/30 space-y-8">
              {/* Questão 1 - Áreas Travadas */}
              {!formData.areasTravadas && (
              <div className="transition-all duration-500 ease-in-out animate-fadeIn">
                <label className="block font-sans text-gray-700 font-medium mb-4">
                  1. Onde você sente que sua vida está mais travada hoje? *
                </label>
                <div className="space-y-3">
                  {[
                    "Em todas as áreas",
                    "Nos relacionamentos",
                    "No dinheiro e carreira",
                    "Na saúde e energia",
                    "No propósito e direção"
                  ].map((option) => (
                    <label key={option} className="flex items-center gap-3 p-3 rounded-lg hover:bg-violet-dust/10 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="areasTravadas"
                        value={option}
                        required
                        onChange={(e) => {
                          handleChange(e)
                          setShowQuestion2(true)
                        }}
                        className="w-4 h-4 text-violet-deep focus:ring-violet-medium"
                      />
                      <span className="font-sans text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
              )}

              {/* Questão 2 - Padrão Repetitivo */}
              {showQuestion2 && !showQuestion3 && (
              <div className="transition-all duration-500 ease-in-out animate-fadeIn">
                <label className="block font-sans text-gray-700 font-medium mb-4">
                  2. Que padrão / ciclo / sofrimento se repete na sua vida e você já está exausta de viver? *
                </label>
                <textarea
                  name="padraoRepetitivo"
                  required
                  value={formData.padraoRepetitivo}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans resize-none"
                  placeholder="Descreva com honestidade o que se repete..."
                />
                {formData.padraoRepetitivo && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setShowQuestion3(true)}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-deep to-violet-medium text-white font-sans font-semibold px-6 py-3 rounded-full hover:shadow-lg transition-all"
                    >
                      Avançar
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Questão 3 - Tentativas de Resolução */}
              {showQuestion3 && !showQuestion4 && (
              <div className="transition-all duration-500 ease-in-out animate-fadeIn">
                <label className="block font-sans text-gray-700 font-medium mb-4">
                  3. O que você já tentou fazer para resolver / sair desse padrão? (Liste cursos, terapias, coachings, práticas espirituais, etc.) *
                </label>
                <textarea
                  name="tentativasResolucao"
                  required
                  value={formData.tentativasResolucao}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans resize-none"
                  placeholder="Liste tudo que já tentou..."
                />
                {formData.tentativasResolucao && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setShowQuestion4(true)}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-deep to-violet-medium text-white font-sans font-semibold px-6 py-3 rounded-full hover:shadow-lg transition-all"
                    >
                      Avançar
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Questão 4 - Nível de Comprometimento */}
              {showQuestion4 && !showQuestion5 && (
              <div className="transition-all duration-500 ease-in-out animate-fadeIn">
                <label className="block font-sans text-gray-700 font-medium mb-4">
                  4. Em uma escala de 0 a 10, qual é o seu nível de comprometimento para mudar isso agora? 0 significa só curiosa. 10 significa que se este processo fizer sentido para mim eu avanço esta semana. *
                </label>
                <input
                  type="text"
                  name="nivelComprometimento"
                  required
                  value={formData.nivelComprometimento}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                  placeholder="Digite um número de 0 a 10"
                />
                {formData.nivelComprometimento && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setShowQuestion5(true)}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-deep to-violet-medium text-white font-sans font-semibold px-6 py-3 rounded-full hover:shadow-lg transition-all"
                    >
                      Avançar
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              )}



              {/* Questão 5 - Uma Coisa */}
              {showQuestion5 && (
              <div className="transition-all duration-500 ease-in-out animate-fadeIn">
                <label className="block font-sans text-gray-700 font-medium mb-4">
                  5. Se você pudesse resolver apenas UMA coisa na sua vida agora, o que seria? *
                </label>
                <textarea
                  name="umaCoisa"
                  required
                  value={formData.umaCoisa}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans resize-none"
                  placeholder="Descreva essa uma coisa..."
                />
              </div>
              )}

              {/* Texto informativo antes do botão */}
              {showQuestion5 && formData.umaCoisa && (
              <div className="text-center pt-6 mt-6">
                <p className="font-sans text-sm text-gray-600 italic mb-6 px-4">
                  Ao enviar suas respostas você está solicitando uma conversa individual comigo. Vou ler tudo com atenção e entrar em contato pessoalmente no WhatsApp em até 48 horas.
                </p>
              </div>
              )}

              {/* Submit Button */}
              {showQuestion5 && formData.umaCoisa && (
              <div className="text-center border-t border-violet-dust/20 pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-rose-soft via-rose-deep to-pink-600 hover:from-pink-600 hover:via-rose-deep hover:to-rose-soft text-white font-sans font-bold text-xl px-14 py-6 rounded-full transition-all duration-300 shadow-[0_0_50px_rgba(236,72,153,0.9)] hover:shadow-[0_0_80px_rgba(236,72,153,1)] transform hover:scale-110 hover:-translate-y-3 animate-pulse disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <span className="relative z-10">{isSubmitting ? 'Enviando...' : 'Estou pronta para ir à raiz'}</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform relative z-10" />
                  <div className="absolute inset-0 bg-white/30 rounded-full blur-xl group-hover:blur-2xl transition-all"></div>
                </button>
              </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
